import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { RegionVariantService } from '../region-variant/region-variant.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _authService: AuthService,
    private regionVariantService: RegionVariantService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation) => {
        this.applyRoles(navigation.default);
        this._navigation.next(navigation);
      }),
    );
  }

  /** Disable menu items according to their role requirements */
  applyRoles(navigationItems: FuseNavigationItem[]): FuseNavigationItem[] {
    const authService = this._authService;

    for (const ni of navigationItems) {
      this.applyRoles(ni.children || []);

      if (ni.meta && ni.meta.roles) {
        let disabled = true;
        for (const role of ni.meta.roles) {
          if (authService.hasRole(role)) {
            disabled = false;
            break;
          }
        }
        ni.disabled = disabled;
        if (disabled) {
          // Disable all children
          const disableNavigationItems = (childItems: FuseNavigationItem[]): void => {
            childItems.forEach((item) => {
              item.disabled = true;
              disableNavigationItems(item.children || []);
            });
          };

          disableNavigationItems(ni.children || []);
        }
      }

      if (ni.meta && ni.meta.visibleRoles) {
        let hidden = true;
        for (const role of ni.meta.visibleRoles) {
          if (authService.hasRole(role)) {
            hidden = false;
          }
        }
        if (hidden) {
          ni.hidden = () => true;
          ni.disabled = true;
        }
      }

      if (ni.meta && ni.meta.regionVariantShow) {
        const hiddenFunc = (_ni) => !this.regionVariantService.show(ni.meta.regionVariantShow);
        const prevHiddenFunc = ni.hidden;
        ni.hidden = prevHiddenFunc ? (_ni) => prevHiddenFunc(_ni) || hiddenFunc(_ni) : hiddenFunc;
      }
    }

    return navigationItems;
  }
}
